import { useForm, Controller } from 'react-hook-form';
import { FormProvider, TextField, Table, Checkbox as RHFCheckbox, SelectTag } from '../../../../../components';
import {
  Button,
  Container,
  Paper,
  Stack,
  Typography,
  Select,
  MenuItem,
  Dialog,
  IconButton,
  Box,
  FormControlLabel,
  Autocomplete,
  TextField as MuiTextField
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import { useEffect, useState, useCallback } from 'react';
import { yupResolver } from '@hookform/resolvers/yup';
import * as Yup from 'yup';
import { BASE_URL, request } from '../../../../../utils';
import { toast } from 'react-toastify';
import axios from 'axios';
import { SelectDoctorTable } from '../../HMS/masters/others/SelectDoctorTable';
import { CustomButtonn } from '../../../../../components';

const TABLE_COLUMNS = [
  {
    header: 'User Name',
    accessor: (row) => `${row.user.first_name} ${row.user.last_name}`
  },
  {
    header: 'Status',
    accessor: 'status',
    sx: { textTransform: 'uppercase' }
  },
  {
    header: 'Created At',
    accessor: 'createdAt'
  }
];

const DOCTOR_TABLE_COLUMN = [
  {
    header: 'Doctor Name',
    accessor: (row) => row.doctor
  }
];

// Lab sections enum you provided
const LAB_SECTIONS = [
  'Hematology',
  'Biochemistry',
  'Microbiology',
  'Immunology',
  'Histopathology',
  'Cytology',
  'Molecular Biology',
  'General'
];

const initialDoctorMappingDataObj = {
  data: [],
  loading: false
};

export const UserAuthorityMaster = () => {
  const [openQueryDialog, setOpenQueryDialog] = useState(false);
  const [doctorMappingDataObj, setDoctorMappingDataObj] = useState(initialDoctorMappingDataObj);
  const [loading, setLoading] = useState(false);
  const [currentAction, setCurrentAction] = useState();
  const [selectedRecord, setSelectedRecord] = useState();
  const [userList, setUserList] = useState([]);
  const [doctorList, setDoctorList] = useState([]);
  const [selectedRows, setSelectedRows] = useState([]); // objects: {_id, name, ok: true}
  const [instrumentOptions, setInstrumentOptions] = useState([]); // dynamic options typed by user

  const methods = useForm({
    resolver: yupResolver(
      Yup.object().shape({
        user: Yup.string().required('User Name is required')
      })
    ),
    defaultValues: {
      user: '',
      signoff: false,
      amend: false,
      permissions: {
        canVerify: false,
        canRelease: false,
        canCancel: false,
        canEditResults: true,
        canEditDates: true
      },
      authorizedSections: [],
      trainedInstruments: []
    },
    mode: 'all'
  });

  const { handleSubmit, setValue, reset, watch, control } = methods;

  // Fetch user list
  const getUserList = useCallback(async () => {
    try {
      const response = await axios.get(`${BASE_URL}/user`);
      setUserList(response.data || []);
    } catch (error) {
      console.error('Error fetching user list:', error);
    }
  }, []);

  // Fetch doctor list (kept as earlier)
  const getDoctorList = useCallback(async () => {
    try {
      const response = await axios.get(`${BASE_URL}/lab-authority-doctor/list`);
      setDoctorList(response.data || []);
    } catch (error) {
      console.error('Error fetching doctor list:', error);
    }
  }, []);

  useEffect(() => {
    getUserList();
    getDoctorList();
  }, [getUserList, getDoctorList]);

  // when a record is selected for edit/view, populate form
  useEffect(() => {
    if (selectedRecord) {
      setValue('user', selectedRecord.user?._id || selectedRecord.user);
      setValue('signoff', selectedRecord.signoff === 'true' || selectedRecord.signoff === true);
      setValue('amend', selectedRecord.amend === 'true' || selectedRecord.amend === true);

      // permissions
      setValue('permissions', {
        canVerify: !!(selectedRecord.permissions && selectedRecord.permissions.canVerify),
        canRelease: !!(selectedRecord.permissions && selectedRecord.permissions.canRelease),
        canCancel: !!(selectedRecord.permissions && selectedRecord.permissions.canCancel),
        canEditResults: selectedRecord.permissions?.canEditResults ?? true,
        canEditDates: selectedRecord.permissions?.canEditDates ?? true
      });

      // sections & instruments
      setValue('authorizedSections', selectedRecord.authorizedSections || []);
      setValue('trainedInstruments', selectedRecord.trainedInstruments || []);
      // prefill instruments options
      setInstrumentOptions(selectedRecord.trainedInstruments || []);

      // selected doctors mapping
      if (selectedRecord.selectedDoctors && Array.isArray(selectedRecord.selectedDoctors)) {
        const initialSelectedRows = selectedRecord.selectedDoctors.map((doc) => ({
          _id: doc._id,
          name: doc.doctor || doc.name || '',
          ok: true
        }));
        setSelectedRows(initialSelectedRows);
      } else {
        setSelectedRows([]);
      }
    } else {
      reset();
      setSelectedRows([]);
      setInstrumentOptions([]);
    }
  }, [selectedRecord, setValue, reset]);

  // Helper: build payload selectedDoctors as array of ids
  const selectedDoctorIds = () => selectedRows.map((r) => r._id);

  // Form submit -> create or update
  const formSubmit = async (formValues) => {
    setLoading(true);
    try {
      const payload = {
        user: formValues.user,
        signoff: formValues.signoff,
        amend: formValues.amend,
        selectedDoctors: selectedDoctorIds(),
        permissions: formValues.permissions,
        authorizedSections: formValues.authorizedSections,
        trainedInstruments: formValues.trainedInstruments,
        status: selectedRecord?.status || 'active'
      };

      if (selectedRecord && currentAction === 'edit') {
        // Update
        const path = `/lab-user-authority-master-update`;
        const response = await request(path, {
          params:selectedRecord._id,
          method: 'PUT',
          body: payload
        });

        if (response?.success) {
          toast.success('Successfully updated User Authority Master');
          setSelectedRecord(response.data);
          setCurrentAction();
        } else {
          toast.error('Failed to update User Authority Master');
        }
      } else {
        // Create
        const path = `/lab-user-authority-master-create`;
        const response = await request(path, {
          method: 'POST',
          body: payload
        });

        if (response?.success) {
          toast.success('Successfully created User Authority Master');
          setSelectedRecord(response.data);
          setCurrentAction();
        } else {
          toast.error('Failed to create User Authority Master');
        }
      }
    } catch (err) {
      console.error(err);
      toast.error('Error while saving User Authority Master');
    } finally {
      setLoading(false);
    }
  };

  // Cancel (backwards-compat endpoint uses DELETE /cancel/:id)
  const handleCancelRecord = async () => {
    if (!selectedRecord) return;
    setLoading(true);
    try {
      const path = `/lab-user-authority-master-cancel`;
      const response = await request(path, {
        params:selectedRecord._id,
        method: 'DELETE'
      });
      if (response?.success) {
        toast.success('Successfully cancelled User Authority Master');
        setSelectedRecord();
      } else {
        toast.error('Failed to cancel User Authority Master');
      }
    } catch (err) {
      console.error(err);
      toast.error('Error cancelling User Authority Master');
    } finally {
      setLoading(false);
    }
  };

  // Delete (soft delete endpoint)
  const handleDeleteRecord = async () => {
    if (!selectedRecord) return;
    setLoading(true);
    try {
      const path = `/lab-user-authority-master-delete`;
      const response = await request(path, {
         params:selectedRecord._id,
        method: 'DELETE'
      });
      if (response?.success) {
        toast.success('Successfully deleted User Authority Master');
        setSelectedRecord();
      } else {
        toast.error('Failed to delete User Authority Master');
      }
    } catch (err) {
      console.error(err);
      toast.error('Error deleting User Authority Master');
    } finally {
      setLoading(false);
    }
  };

  // Deactivate (explicit route exists)
  const handleDeactivateRecord = async () => {
    if (!selectedRecord) return;
    setLoading(true);
    try {
      const path = `/lab-user-authority-master-deactivate`;
      const response = await request(path, {
         params:selectedRecord._id,
        method: 'PUT'
      });
      if (response?.success) {
        toast.success('Successfully deactivated User Authority Master');
        setSelectedRecord(response.data);
      } else {
        toast.error('Failed to deactivate User Authority Master');
      }
    } catch (err) {
      console.error(err);
      toast.error('Error deactivating User Authority Master');
    } finally {
      setLoading(false);
    }
  };

  // Query window (list)
  const handleQueryWindow = async () => {
    setOpenQueryDialog(true);
    setDoctorMappingDataObj({ loading: true, data: [] });
    try {
      const response = await request('/lab-user-authority-master-list', { method: 'GET' });
      if (response?.success) {
        const filteredData = response.data.filter(
          (item) => item.status !== 'deleted' // default filtering
        );
        setDoctorMappingDataObj({ loading: false, data: filteredData });
      } else {
        toast.error('Error fetching data');
        setDoctorMappingDataObj({ loading: false, data: [] });
      }
    } catch (err) {
      console.error(err);
      toast.error('Error fetching data');
      setDoctorMappingDataObj({ loading: false, data: [] });
    }
  };

  // Helpers for SelectDoctorTable selection
  const isSelected = (row, field) => {
    if (!Array.isArray(selectedRows)) return false;
    const sel = selectedRows.find((r) => r._id === row._id);
    return sel ? !!sel[field] : false;
  };

  const handleRowSelect = (row, field) => {
    if (!row || !field) return;
    if (!Array.isArray(selectedRows)) {
      setSelectedRows([]);
    }
    setSelectedRows((prev) => {
      const existing = prev.find((r) => r._id === row._id);
      if (existing) {
        if (existing[field]) {
          return prev.filter((r) => r._id !== row._id);
        } else {
          return prev.map((r) => (r._id === row._id ? { ...r, [field]: true } : r));
        }
      } else {
        return [...prev, { ...row, [field]: true }];
      }
    });
  };

  return (
    <>
      <Paper
        sx={{
          p: 0,
          flex: 1,
          borderRadius: 0,
          boxShadow: 'none',
          width: '100%',
          height: 'auto'
        }}
      >
        <Container
          sx={{
            mt: 1,
            border: '0px solid',
            borderColor: 'grey.300',
            borderRadius: 1,
            width: '100%',
            maxWidth: 'none',
            minWidth: '100%',
            boxSizing: 'border-box'
          }}
        >
          <FormProvider methods={methods} onSubmit={handleSubmit(formSubmit)}>
            <Stack
              direction="row"
              spacing={2}
              justifyContent="space-between"
              alignItems="center"
              sx={{
                mb: 1,
                borderBottom: '0px solid',
                borderColor: 'grey.300'
              }}
            >
              <Typography color="primary" variant="h3" sx={{ fontWeight: 600 }}>
                User Authority Master
              </Typography>
              <Stack direction="row" justifyContent="flex-end" sx={{ mb: 3 }} spacing={1}>
                <CustomButtonn
                  id={`newRecord`}
                  variant="contained"
                  disabled={['edit', 'new'].includes(currentAction)}
                  onClick={() => {
                    setSelectedRecord();
                    setCurrentAction('new');
                    setSelectedRows([]);
                    reset();
                  }}
                >
                  New
                </CustomButtonn>
                <CustomButtonn
                  id={`editRecord`}
                  variant="contained"
                  disabled={['new', 'edit'].includes(currentAction) || !selectedRecord}
                  onClick={() => setCurrentAction('edit')}
                >
                  Edit
                </CustomButtonn>
                <CustomButtonn
                  id={'Submit'}
                  type="submit"
                  variant="contained"
                  disabled={!['edit', 'new'].includes(currentAction) || loading}
                >
                  Save
                </CustomButtonn>
                <CustomButtonn
                  id={'Cancel'}
                  variant="contained"
                  disabled={['new', 'edit'].includes(currentAction) || loading}
                  onClick={() => {
                    setCurrentAction();
                    handleCancelRecord();
                  }}
                >
                  Cancel
                </CustomButtonn>
                <CustomButtonn
                  id={'Delete'}
                  variant="contained"
                  disabled={!selectedRecord || ['new', 'edit'].includes(currentAction) || loading}
                  onClick={handleDeleteRecord}
                >
                  Delete
                </CustomButtonn>
                <CustomButtonn
                  id={'Deactivate'}
                  variant="contained"
                  disabled={!selectedRecord || ['new', 'edit'].includes(currentAction) || loading}
                  onClick={handleDeactivateRecord}
                >
                  Deactivate
                </CustomButtonn>
                <CustomButtonn
                  id={`Query`}
                  variant="contained"
                  onClick={handleQueryWindow}
                  disabled={['new', 'edit'].includes(currentAction)}
                >
                  Query
                </CustomButtonn>
                <CustomButtonn
                  id={`Print`}
                  variant="contained"
                  disabled={!selectedRecord || currentAction}
                >
                  Print
                </CustomButtonn>
                <CustomButtonn
                  id={'Exit'}
                  variant="contained"
                  onClick={() => {
                    setCurrentAction('');
                    setSelectedRecord();
                  }}
                >
                  Exit
                </CustomButtonn>
              </Stack>
            </Stack>

            <Stack gap={0.1}>
              <Box
                sx={{
                  border: '0px solid gray',
                  padding: 2,
                  borderRadius: '8px',
                  width: '70%',
                  boxShadow: '0px 2px 10px #0099ff'
                }}
              >
                <Stack spacing={2} direction="row" alignItems="center" sx={{ marginBottom: '1%' }}>
                  <Typography sx={{ flex: 0.2, fontSize: '1.4rem', fontWeight: 700 }}>Type</Typography>

                  <Stack direction="row" spacing={2} alignItems="center">
                    <Typography sx={{ flex: 0.25, fontSize: '1.1rem', fontWeight: 700 }}>SignOff</Typography>
                    <Controller
                      name="signoff"
                      control={control}
                      render={({ field }) => (
                        <FormControlLabel
                          control={<input type="checkbox" {...field} disabled={!['new', 'edit'].includes(currentAction)} />}
                          label=""
                        />
                      )}
                    />
                  </Stack>

                  <Stack direction="row" spacing={2} alignItems="center">
                    <Typography sx={{ flex: 0.25, fontSize: '1.1rem', fontWeight: 700 }}>Amend</Typography>
                    <Controller
                      name="amend"
                      control={control}
                      render={({ field }) => (
                        <FormControlLabel
                          control={<input type="checkbox" {...field} disabled={!['new', 'edit'].includes(currentAction)} />}
                          label=""
                        />
                      )}
                    />
                  </Stack>
                </Stack>

                <Stack spacing={2} direction="row" alignItems="center">
                  <Typography sx={{ flex: 0.15, fontSize: '1.4rem', fontWeight: 700 }}>Users</Typography>
                  <Controller
                    name="user"
                    control={control}
                    render={({ field }) => (
                      <SelectTag
                        disabled={!['new', 'edit'].includes(currentAction)}
                        formControlSX={{ flex: 0.7 }}
                        name="user"
                        value={field.value}
                        onChange={(val) => field.onChange(val)}
                        menus={userList.map((data) => ({ ...data, name: `${data.first_name} ${data.last_name}` }))}
                      />
                    )}
                  />
                </Stack>

                <Stack spacing={2} direction="column" sx={{ mt: 2 }}>
                  <Typography sx={{ fontWeight: 700 }}>Permissions</Typography>
                  <Stack direction="row" spacing={2} alignItems="center">
                    <Controller
                      name="permissions.canVerify"
                      control={control}
                      render={({ field }) => (
                        <FormControlLabel
                          control={<input type="checkbox" {...field} checked={!!field.value} disabled={!['new', 'edit'].includes(currentAction)} />}
                          label="Can Verify"
                        />
                      )}
                    />
                    <Controller
                      name="permissions.canRelease"
                      control={control}
                      render={({ field }) => (
                        <FormControlLabel
                          control={<input type="checkbox" {...field} checked={!!field.value} disabled={!['new', 'edit'].includes(currentAction)} />}
                          label="Can Release"
                        />
                      )}
                    />
                    <Controller
                      name="permissions.canCancel"
                      control={control}
                      render={({ field }) => (
                        <FormControlLabel
                          control={<input type="checkbox" {...field} checked={!!field.value} disabled={!['new', 'edit'].includes(currentAction)} />}
                          label="Can Cancel"
                        />
                      )}
                    />
                    <Controller
                      name="permissions.canEditResults"
                      control={control}
                      render={({ field }) => (
                        <FormControlLabel
                          control={<input type="checkbox" {...field} checked={!!field.value} disabled={!['new', 'edit'].includes(currentAction)} />}
                          label="Can Edit Results"
                        />
                      )}
                    />
                    <Controller
                      name="permissions.canEditDates"
                      control={control}
                      render={({ field }) => (
                        <FormControlLabel
                          control={<input type="checkbox" {...field} checked={!!field.value} disabled={!['new', 'edit'].includes(currentAction)} />}
                          label="Can Edit Dates"
                        />
                      )}
                    />
                  </Stack>
                </Stack>

                <Stack spacing={2} direction="column" sx={{ mt: 2 }}>
                  <Typography sx={{ fontWeight: 700 }}>Authorized Sections</Typography>
                  <Controller
                    name="authorizedSections"
                    control={control}
                    render={({ field }) => (
                      <Autocomplete
                        multiple
                        disabled={!['new', 'edit'].includes(currentAction)}
                        options={LAB_SECTIONS}
                        value={field.value}
                        onChange={(e, newVal) => field.onChange(newVal)}
                        renderInput={(params) => <MuiTextField {...params} placeholder="Select lab sections" />}
                      />
                    )}
                  />
                </Stack>

                <Stack spacing={2} direction="column" sx={{ mt: 2 }}>
                  <Typography sx={{ fontWeight: 700 }}>Trained Instruments</Typography>
                  <Controller
                    name="trainedInstruments"
                    control={control}
                    render={({ field }) => (
                      <Autocomplete
                        multiple
                        freeSolo
                        disabled={!['new', 'edit'].includes(currentAction)}
                        options={instrumentOptions}
                        value={field.value}
                        onChange={(e, newVal) => {
                          // Add new typed-in instrument options to options list
                          const newOptions = Array.from(new Set([...instrumentOptions, ...newVal.filter((v) => typeof v === 'string')]));
                          setInstrumentOptions(newOptions);
                          field.onChange(newVal);
                        }}
                        renderInput={(params) => <MuiTextField {...params} placeholder="Type or select instruments" />}
                      />
                    )}
                  />
                </Stack>

                <Stack
                  padding={0.2}
                  direction="row"
                  spacing={2}
                  alignItems="center"
                  justifyContent="space-between"
                  borderBottom="1px solid"
                  borderColor="grey.300"
                  marginTop="2%"
                >
                  <SelectDoctorTable
                    sx={{ mt: 0.5 }}
                    columns={DOCTOR_TABLE_COLUMN}
                    data={!(watch('type') === 'amend') && watch('user') ? doctorList.data || doctorList : []}
                    loading={doctorMappingDataObj.loading}
                    showOk={true}
                    isSelected={isSelected}
                    handleRowSelect={handleRowSelect}
                    currentAction={currentAction}
                  />
                </Stack>
              </Box>
            </Stack>
          </FormProvider>
        </Container>
      </Paper>

      <Dialog
        open={openQueryDialog}
        onClose={() => setOpenQueryDialog(false)}
        PaperProps={{ sx: { width: '80%', p: 1.5 } }}
        maxWidth="xl"
        scroll="body"
      >
        <Stack
          padding={0.2}
          direction="row"
          spacing={2}
          alignItems="center"
          justifyContent="space-between"
          borderBottom="1px solid"
          borderColor="grey.300"
        >
          <Typography variant="h4" fontWeight="bold">
            User Authority Master Query Window
          </Typography>
          <IconButton onClick={() => setOpenQueryDialog(false)}>
            <CloseIcon color="error" fontSize="large" />
          </IconButton>
        </Stack>
        <Table
          sx={{ mt: 0.5 }}
          columns={TABLE_COLUMNS}
          data={doctorMappingDataObj.data || []}
          loading={doctorMappingDataObj.loading}
          onRowClick={(row) => {
            setSelectedRecord(row);
            setOpenQueryDialog(false);
          }}
        />
      </Dialog>
    </>
  );
};
